import Errored from '../../core/Errored';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../../application-core/constants';
import Loading from '../../core/Loading';
import TitledPanel from '../../core/TitledPanel';
import { getGraphFromProject, makeCancelable, sortObjects } from '../../core/utils/utils';
import { CONCEPT_LOAD_OPTIONS } from '../../core/utils/constants';
import GS from '../../grafsync/grafsync';
import ConceptLink from '../core/ConceptLink';
import OtherProjectLink from '../../core/OtherProjectLink';
import ExternalResourceLink from '../../core/ExternalResourceLink';
import FindRelatedConceptDialog from './FindRelatedConceptDialog';
import FloatingRelationshipLink from './FloatingRelationshipsLink';
import DropConceptLink from './DropConceptLink';
import { Typography } from '@material-ui/core';

const MAX_NODES = 5000;

class RelationshipsDisplay extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      initialLoad: true,
      isLoading: props.values && props.values.length > 0 && props.values.length < 5001,
      totalRelations: 0,
      unloadedResources: [],
      hasConcepts: undefined,
      concepts: undefined,
      viewLang: undefined,
      isErrored: undefined
    };
    this.cancellable = null;
  }

  static getDerivedStateFromProps(props, state) {
    if ((props.values && (props.values.length !== state.totalRelations || props.viewLang !== state.viewLang))
      || (!props.values && state.totalRelations > 0)) {
      return {
        hasConcepts: undefined,
        ...(props.values.length === 0) && {
          concepts: null,
          unloadedResources: []
        },
        isLoading: props.values && props.values.length > 0 && props.values.length < 5001,
        viewLang: props.viewLang,
        ...(props.values) && ({ totalRelations: props.values.length })
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (this.props.values && this.props.values.length > 0 && this.props.values.length < 5001 && this.state.hasConcepts === undefined) {
      this.collectConceptData();
    }
  }

  componentDidMount() {
    if (this.props.values.length < 5001) {
      this.collectConceptData();
    }
  }

  loadData(iris, options, graph) {
    if (!graph) graph = this.props.graph;
    this.cancellable = makeCancelable(GS.loadData(iris, options, graph, true));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  async collectConceptData() {
    const { viewLang, defaultLang, values, associatedProject } = this.props;
    // We load ALL the concepts necessary at this level in the tree in one API call to avoid unnecessary calls
    const mergedLoadOptions = Object.assign({}, CONCEPT_LOAD_OPTIONS, { language: viewLang ? [defaultLang, viewLang] : defaultLang });
    // We load ALL the resources necessary at this level in the tree in one API call to avoid unnecessary calls
    // There may be more than 5000 children so we need to load in pages just in case!
    try {
      const pages = Math.floor((values.length + MAX_NODES - 1) / MAX_NODES);
      for (let page = 0; page < pages; page++) {
        const pageStart = page * MAX_NODES;
        const pageEnd = (page + 1) * MAX_NODES;
        const pageChildren = values.slice(pageStart, pageEnd);
        await this.loadData(pageChildren, mergedLoadOptions).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
      }
      const linkedProjects = associatedProject ? [...associatedProject].sort() : [];
      let doesNotExist = values ? values.filter(iri => !GS.isLoaded(iri, this.props.graph)) : [];
      let allDoesNotExist = [...doesNotExist];
      let lpCounter = 0;
      let graphInfo = {};
      while (doesNotExist.length > 0 && lpCounter < linkedProjects.length) {
        const linkedGraph = getGraphFromProject(linkedProjects[lpCounter]);
        // We also load the linked project too
        await this.loadData([...doesNotExist, linkedProjects[lpCounter]], CONCEPT_LOAD_OPTIONS, linkedGraph).promise;
        const doesNowExist = doesNotExist.filter(iri => GS.isLoaded(iri, linkedGraph));
        doesNowExist.forEach(iri => graphInfo[iri] = linkedGraph);
        doesNotExist = doesNotExist.filter(iri => !GS.isLoaded(iri, linkedGraph));
        lpCounter++;
      }
      if (this.cancellable && this.cancellable.isCancelled()) return;
      this.setState({ unloadedResources: allDoesNotExist, graphInfo });
      this.processConcepts();
      this.setState({ isLoading: false, initialLoad: false });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  }

  processConcepts() {
    if (this.cancellable && this.cancellable.isCancelled()) return;
    const doesNotExist = this.props.values.filter(iri => !GS.isLoaded(iri, this.props.graph));
    let concepts = this.props.values
      .filter(id => !doesNotExist.includes(id))
      .filter(id => {
        const conceptData = GS.data(id, this.props.graph);
        const types = Array.isArray(conceptData.type) ? conceptData.type : [conceptData.type];
        return types.includes("Concept");
      })
      .map(id => GS.data(id, this.props.graph));
    const conceptList = concepts.map(concept => concept.id);
    const nonConceptResources = this.props.values.filter(id => !doesNotExist.includes(id) && !conceptList.includes(id));
    sortObjects(concepts, 'prefLabel', this.props.viewLang, this.props.defaultLang, false);
    this.setState({ unloadedResources: doesNotExist, nonConceptResources, concepts, hasConcepts: true });
  }

  monitorLinkUpdate = (iri, property) => {
    if (property === "prefLabel") this.processConcepts();
  };

  render() {

    const { concepts, isLoading, unloadedResources, graphInfo, isErrored, initialLoad, nonConceptResources } = this.state;
    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading && initialLoad) return <Loading />;

    const { components, existingRelationships, isReadOnly, defaultLang, viewLang, graph, permitExternal,
      onAddToConcept, onAddLabelToConcept, associatedProject, linkedData, values, useWorkflow, additionalDisplay,
      title, projectIRI, matching, includeExistingConcept, schemeIRI, source, property, addTitle, dataTest, highlight } = this.props;
    const exclusionList = concepts ? concepts.map(concept => concept.id) : [];
    const ers = existingRelationships || [];
    const propertyGraph = getGraphFromProject(projectIRI);
    const exclude = [...new Set([...exclusionList, ...ers, source, ...unloadedResources])];

    return (
      <TitledPanel
        dataTest={dataTest}
        highlight={highlight}
        height="full"
        title={title}
        actions=
        {!isReadOnly ?
          <FindRelatedConceptDialog
            addTitle={addTitle}
            source={source}
            graph={graph}
            useWorkflow={useWorkflow}
            components={components}
            schemeIRI={schemeIRI}
            linkedData={linkedData}
            associatedProject={associatedProject}
            permitExternal={permitExternal}
            matching={matching}
            exclude={exclude}
            viewLang={viewLang}
            onIncludeConcept={includeExistingConcept} />
          :
          null
        }>
        {concepts && concepts
          .filter(concept => concept.inScheme && concept.inScheme.length > 0)
          .map((concept, index) => {
            // Max 500 displayed
            if (index >= 500) return null;
            const isProjectLink = components && concept.inScheme.some(iri => components.includes(iri));
            let preferredScheme;
            if (Array.isArray(concept.inScheme)) {
              if (concept.inScheme.some(iri => iri === schemeIRI)) preferredScheme = schemeIRI;
              else preferredScheme = concept.inScheme[0];
            }
            else preferredScheme = concept.inScheme;
            const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' + projectIRI + "&taxonomy="
              + preferredScheme + "&view=concept&concept=" + concept.id;
            // Is link within schemes in project?
            if (isProjectLink) {
              return isReadOnly || !onAddToConcept || !concept.inScheme.includes(schemeIRI) ?
                <div style={{ color: 'green', display: 'inline-block', maxWidth: '100%' }} key={concept.id}>
                  <ConceptLink
                    link={link}
                    id={concept.id}
                    graph={graph}
                    useWorkflow={useWorkflow}
                    otherScheme={!concept.inScheme.includes(schemeIRI)}
                    propertyGraph={propertyGraph}
                    isReadOnly={isReadOnly}
                    source={source}
                    target={concept.id}
                    updateEvent={this.monitorLinkUpdate}
                    property={property}
                    loadOptions={{
                      language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang
                    }}
                    viewLang={viewLang}
                    defaultLang={defaultLang} />
                </div>
                :
                <div style={{ display: 'inline-block', maxWidth: '100%' }} key={concept.id}>
                  <DropConceptLink
                    onAddToConcept={onAddToConcept}
                    onAddLabelToConcept={onAddLabelToConcept}
                    link={link}
                    id={concept.id}
                    graph={graph}
                    useWorkflow={useWorkflow}
                    otherScheme={!concept.inScheme.includes(schemeIRI)}
                    propertyGraph={propertyGraph}
                    isReadOnly={isReadOnly}
                    source={source}
                    target={concept.id}
                    updateEvent={this.monitorLinkUpdate}
                    property={property}
                    loadOptions={{
                      language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang
                    }}
                    viewLang={viewLang}
                    defaultLang={defaultLang} />
                </div>;
            }
            // We shouldn't get to this line because should be an unloaded resource
            else return null;
          })}
        {values && values.length > 5000 &&
          <Typography data-test="too-many-relations-to-display" variant="caption">Too many relationships to display.</Typography>
        }
        {concepts && concepts.length > 500 &&
          <Typography data-test="too-many-relations" variant="caption">More relationships were found ({concepts.length - 500})</Typography>
        }
        {concepts && concepts
          .filter(concept => !concept.inScheme || concept.inScheme.length === 0)
          .map(concept => {
            const link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + projectIRI + "&view=concept&concept=" + concept.id;
            // Is concept a free floating concept?
            return <span key={concept.id}>
              <FloatingRelationshipLink
                id={concept.id}
                link={link}
                graph={graph}
                isReadOnly={isReadOnly}
                propertyGraph={propertyGraph}
                source={source}
                target={concept.id}
                updateEvent={this.monitorLinkUpdate}
                property={property}
                viewLang={viewLang}
                defaultLang={defaultLang} />
            </span>;
          }
          )}
        {nonConceptResources && [...nonConceptResources].sort().map(iri =>
          <ExternalResourceLink
            key={"rel-" + source + "-" + iri}
            id={iri}
            graph={graph}
            isReadOnly={isReadOnly}
            source={source}
            property={property} />
        )}
        {unloadedResources && [...unloadedResources].sort().map((iri) =>
          graphInfo[iri] ?
            <OtherProjectLink
              id={iri}
              key={iri}
              graph={graphInfo[iri]}
              sourceGraph={graph}
              defaultLang={defaultLang}
              viewLang={viewLang}
              source={source}
              isReadOnly={isReadOnly}
              property={property} />
            :
            <ExternalResourceLink
              key={"rel-" + source + "-" + iri}
              id={iri}
              graph={graph}
              isReadOnly={isReadOnly}
              source={source}
              property={property} />
        )}
        {additionalDisplay}
      </TitledPanel>
    );
  }
}

RelationshipsDisplay.defaultProps = {
  highlight: false,
  permitExternal: false,
  useWorkflow: false,
  associatedProject: [],
  values: []
};

RelationshipsDisplay.propTypes = {
  highlight: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  onAddToConcept: PropTypes.func,
  onAddLabelToConcept: PropTypes.func,
  dataTest: PropTypes.string,
  schemeIRI: PropTypes.string,
  graph: PropTypes.string.isRequired,
  components: PropTypes.array,
  title: PropTypes.string.isRequired,
  permitExternal: PropTypes.bool,
  viewLang: PropTypes.string,
  property: PropTypes.string,
  linkedData: PropTypes.array,
  values: PropTypes.array,
  associatedProject: PropTypes.array,
  defaultLang: PropTypes.string.isRequired,
  includeExistingConcept: PropTypes.func.isRequired,
  addTitle: PropTypes.string,
  projectIRI: PropTypes.string.isRequired
};

export default RelationshipsDisplay;